//Exercise Modal with media type handling
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DailyExercise, MEDIA_TYPE_NAMES } from '../../types';
import { GAME_CONFIG } from '../../utils/gameConfig';

interface DailyExerciseModalProps {
  isOpen: boolean;
  exercise: DailyExercise | null;
  dayNumber: number;
  onClose: () => void;
  onComplete: () => void;
}

const DailyExerciseModal: React.FC<DailyExerciseModalProps> = ({
  isOpen,
  exercise,
  dayNumber,
  onClose,
  onComplete
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleClose = useCallback(async () => {
    if (!exercise) return;
    
    setIsCompleting(true);
    
    //Award seed for completing exercise
    try {
      await onComplete();
      console.log(`Exercise Day ${dayNumber} completed! Seed awarded.`);
    } catch (error) {
      console.error('Error completing exercise:', error);
    }
    
    setIsCompleting(false);
    onClose();
  }, [exercise, dayNumber, onComplete, onClose]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      console.log('File uploaded:', file.name);
    }
  };

  const renderExerciseContent = () => {
    if (!exercise) return null;

    switch (exercise.mediaType) {
      case 0: //Written instructions with file upload
        return (
          <div className="space-y-6">
            <div className="prose max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {exercise.instructions || 'No instructions provided.'}
              </div>
            </div>
            
            {/* File Upload Section */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 mb-4">
                  <svg className="w-full h-full text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    Upload your completed work
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    PDF, DOC, DOCX, or image files
                  </span>
                </label>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                />
              </div>
              
              {uploadedFile && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-green-800">
                      Uploaded: {uploadedFile.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 1: //Video (YouTube, Vimeo, etc.)
        const videoId = extractVideoId(exercise.mediaUrl);
        return (
          <div className="space-y-4">
            {exercise.instructions && (
              <div className="text-gray-700 text-sm bg-blue-50 p-4 rounded-lg">
                {exercise.instructions}
              </div>
            )}
            
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              {videoId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={exercise.title}
                  className="absolute inset-0 w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-center">
                    <p className="mb-4">Video content</p>
                    <a 
                      href={exercise.mediaUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Open Video
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 2: //Written instructions only
        return (
          <div className="prose max-w-none">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {exercise.instructions || 'No instructions provided.'}
            </div>
          </div>
        );

      case 3: //Google Slides
        return (
          <div className="space-y-4">
            {exercise.instructions && (
              <div className="text-gray-700 text-sm bg-blue-50 p-4 rounded-lg">
                {exercise.instructions}
              </div>
            )}
            
            <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src={convertGoogleSlidesUrl(exercise.mediaUrl)}
                title={exercise.title}
                className="absolute inset-0 w-full h-full"
                allowFullScreen
              />
            </div>
          </div>
        );

      case 4: //Google Doc
        return (
          <div className="space-y-4">
            {exercise.instructions && (
              <div className="text-gray-700 text-sm bg-blue-50 p-4 rounded-lg">
                {exercise.instructions}
              </div>
            )}
            
            <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src={convertGoogleDocUrl(exercise.mediaUrl)}
                title={exercise.title}
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        );

      case 5: //Generic URL
        return (
          <div className="space-y-4">
            {exercise.instructions && (
              <div className="text-gray-700 text-sm bg-blue-50 p-4 rounded-lg">
                {exercise.instructions}
              </div>
            )}
            
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 mb-4">
                <svg className="w-full h-full text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">External Resource</h3>
              <p className="text-gray-600 mb-4">Click the link below to access the exercise content</p>
              <a
                href={exercise.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Resource
              </a>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-gray-500">
            <p>Unsupported media type: {exercise.mediaType}</p>
          </div>
        );
    }
  };

  if (!exercise) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-4xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Day {dayNumber}: {exercise.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {MEDIA_TYPE_NAMES[exercise.mediaType]}
                  </p>
                </div>
                
                <button
                  onClick={handleClose}
                  disabled={isCompleting}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                {renderExerciseContent()}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Complete this exercise to earn 1 seed
                </div>
                
                <button
                  onClick={handleClose}
                  disabled={isCompleting}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isCompleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Complete Exercise
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

//Helper functions
const extractVideoId = (url: string): string | null => {
  const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
  const match = url.match(youtubeRegex);
  return match ? match[1] : null;
};

const convertGoogleSlidesUrl = (url: string): string => {
  //Convert Google Slides sharing URL to embed URL
  if (url.includes('/edit')) {
    return url.replace('/edit', '/embed');
  }
  return url;
};

const convertGoogleDocUrl = (url: string): string => {
  //Convert Google Docs sharing URL to embed URL
  if (url.includes('/edit')) {
    return url.replace('/edit', '/preview');
  }
  return url;
};

export default DailyExerciseModal;